/**
 * Shared billing code identifier helpers.
 *
 * Keep these semantics aligned with the backend charges/code_id utility.
 */

const HCPCS_PATTERN = /^[A-EGHJ-TVUX][0-9]{4}$/;
const SUPPORTED_CODE_SYSTEMS = new Set(['CPT', 'HCPCS']);

export const stripCodeSystem = (codeId: string): string => {
  const colonIndex = codeId.indexOf(':');
  return colonIndex !== -1 ? codeId.slice(colonIndex + 1) : codeId;
};

export const sanitizeBillingCodeInput = (value: string): string =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);

export const normalizeToCodeId = (code: string): string => {
  const trimmed = (code ?? '').trim().toUpperCase();
  if (!trimmed) return '';

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex !== -1) {
    const suppliedSystem = trimmed.slice(0, colonIndex);
    const suppliedCode = trimmed.slice(colonIndex + 1).trim();
    if (!suppliedCode) return '';
    // Preserve unsupported legacy identifiers during unrelated edits. New
    // picker input cannot create these prefixes, and callers can surface
    // explicit validation instead of silently rewriting the identifier.
    if (!SUPPORTED_CODE_SYSTEMS.has(suppliedSystem)) return `${suppliedSystem}:${suppliedCode}`;

    const bare = sanitizeBillingCodeInput(suppliedCode);
    if (!bare) return '';
    const system = suppliedSystem === 'CPT' && HCPCS_PATTERN.test(bare) ? 'HCPCS' : suppliedSystem;
    return `${system}:${bare}`;
  }

  const bare = sanitizeBillingCodeInput(trimmed);
  if (!bare) return '';
  return HCPCS_PATTERN.test(bare) ? `HCPCS:${bare}` : `CPT:${bare}`;
};
